import type { AdvizeoDialogModalInput } from '@/components/modals/advizeo-dialog-modal/advizeo-dialog-modal';
import { SharedUiColorNameV2 } from '@/shared/ui/theming';
import { assertInInjectionContext, inject, Injectable, type Signal, type Type } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, type MatDialogConfig, type MatDialogRef } from '@angular/material/dialog';
import { firstValueFrom, Subject } from 'rxjs';
import { filter, last, takeUntil } from 'rxjs/operators';
import {
    type AdvizeoModalComponent,
    AdvizeoModalSpawnerComponent
} from '../components/advizeo-modal-spawner/advizeo-modal-spawner.component';
import {
    ConfirmActionPopoverComponent,
    type ConfirmActionPopoverInput
} from '../components/popovers/generic-popovers/confirm-action-popover/confirm-action-popover';
import {
    OneFieldPopoverComponent,
    type OneFieldPopoverInput,
    type OneFieldPopoverOutput
} from '../components/popovers/generic-popovers/one-field-popover/one-field-popover';
import { AdvizeoUiElementSize } from '../interfaces/AdvizeoUi.interface';
import type { AdvizeoModal, ModalComponent, ModalConfig } from '../interfaces/Modal.interface';
import { AdvizeoDialogModalComponent } from './../components/modals/advizeo-dialog-modal/advizeo-dialog-modal';
import type { AdvizeoLegacyDialogConfig } from './../interfaces/Modal.interface';

export function injectModalInput<T>() {
    assertInInjectionContext(injectModalInput);
    const data = inject<T>(MAT_DIALOG_DATA);
    return data;
}

type ExtractSignalType<T extends Signal<any>> = T extends Signal<infer U> ? U : never;

@Injectable({
    providedIn: 'root'
})
export class AdvizeoModalService {
    private _dialog = inject(MatDialog);

    openModal<Input, Output>(
        component: Type<ModalComponent<Input, Output>>,
        inputData: Input,
        { size = AdvizeoUiElementSize.MEDIUM, noPadding = false, disableClose = false, panelClass = '' }: ModalConfig = {}
    ): AdvizeoModal<Output> {
        const dialogConfig: MatDialogConfig = {
            panelClass: ['advizeo-modal', 'modal-size-' + size, panelClass, noPadding ? 'advizeo-modal-no-padding' : ''].filter(Boolean),
            autoFocus: false,
            disableClose,
            data: inputData
        };

        const modal = this._dialog.open(component, dialogConfig);

        const advizeoModal: AdvizeoModal<Output> = this.createAdvizeoModal<Output>(modal);

        modal.componentInstance.close.subscribe(output => {
            advizeoModal.close(output);
        });
        return advizeoModal;
    }

    openAdvizeoModal<Input, Output>(
        modalComponent: Type<AdvizeoModalComponent<Input, Output>>,
        modalComponentInputData?: ExtractSignalType<AdvizeoModalComponent<Input, Output>['input']>,
        modalConfig?: ModalConfig
    ): AdvizeoModal<Output> {
        return this.openModal(
            AdvizeoModalSpawnerComponent as Type<ModalComponent<any, any>>,
            {
                modalComponent,
                modalComponentInput: modalComponentInputData
            },
            modalConfig
        );
    }

    openOneFieldModal(inputData: OneFieldPopoverInput): AdvizeoModal<OneFieldPopoverOutput> {
        return this.openAdvizeoModal(OneFieldPopoverComponent, inputData, { size: AdvizeoUiElementSize.SMALL });
    }

    openConfirmActionModal(inputData: ConfirmActionPopoverInput): AdvizeoModal<string> {
        return this.openAdvizeoModal(ConfirmActionPopoverComponent, inputData);
    }

    openDialog(dialogConfig: AdvizeoDialogModalInput, size: AdvizeoUiElementSize = AdvizeoUiElementSize.SMALL): AdvizeoModal<boolean> {
        return this.openAdvizeoModal(AdvizeoDialogModalComponent, dialogConfig, { size });
    }

    /**
     * @deprecated Use openDialog instead
     */
    openLegacyDialog(legacyDialogConfig: AdvizeoLegacyDialogConfig): AdvizeoModal<boolean> {
        return this.openDialog({
            title: legacyDialogConfig.title,
            message:
                legacyDialogConfig.subTitle && legacyDialogConfig.message
                    ? `${legacyDialogConfig.subTitle}\n${legacyDialogConfig.message}`
                    : legacyDialogConfig.subTitle
                      ? legacyDialogConfig.subTitle
                      : legacyDialogConfig.message,
            buttons: legacyDialogConfig.buttons
                .sort((b1, b2) => (b1.role === 'cancel' ? 1 : b2.role === 'cancel' ? -1 : 0))
                .map(legacyButton => ({
                    text: legacyButton.text,
                    onClick: legacyButton.handler
                        ? legacyButton.handler
                        : legacyButton.role === 'cancel'
                          ? ('cancel' as 'cancel')
                          : () => {},
                    color: SharedUiColorNameV2.PRIMARY
                }))
        });
    }

    private createAdvizeoModal<Output>(matDialogRef: MatDialogRef<any, any>): AdvizeoModal<Output> {
        const result$ = new Subject<Output | undefined>();

        const close = (output: Output | undefined) => {
            matDialogRef.close();
            result$.next(output);
            result$.complete();
        };
        matDialogRef
            .backdropClick()
            .pipe(
                filter(() => !matDialogRef.disableClose),
                takeUntil(result$.pipe(last(undefined, null)))
            ) // unsubscribe when result$ completes
            .subscribe(() => {
                close(undefined);
            });

        const afterClosed: () => Promise<Output | undefined> = () => {
            return firstValueFrom(result$.asObservable());
        };

        return {
            close,
            afterClosed
        };
    }
}
